// The landing's pricing: credit packs from GET /api/credits/packs (public), the same list the Credits page sells.
// Credits are bought once (checkout), not by subscription. The page shows Creator and Pro as cards and the
// other packs as a line under them. If the api doesn't answer, it shows STATIC_PACKS (the v3 price table the
// integrator handed over, 2026-10-29). Pure: no DOM, no React.
import { AD_PRICES } from "./content";

export type Pack = { id: string; name: string; credits: number; usdCents: number };

export const STATIC_PACKS: Pack[] = [
  { id: "topup-1250", name: "1,250 credits", credits: 1250, usdCents: 1500 },
  { id: "creator", name: "Creator", credits: 2900, usdCents: 2900 },
  { id: "topup-5000", name: "5,000 credits", credits: 5000, usdCents: 5500 },
  { id: "pro", name: "Pro", credits: 10000, usdCents: 9900 },
  { id: "topup-15000", name: "15,000 credits", credits: 15000, usdCents: 15000 },
];

/** The packs shown as cards, in order (the rest go in the "other packs" line). */
export const CARD_PACKS = ["creator", "pro"] as const;
export const FEATURED_PACK = "pro";

/** Talking with the Director, per minute (core pricing `directorMinute`). */
export const DIRECTOR_CREDITS_PER_MINUTE = 30;
/** 2026-11-02: typing to the Director instead, per minute (core pricing `directorTextMinute`). */
export const DIRECTOR_TEXT_CREDITS_PER_MINUTE = 10;

export function parsePacks(json: unknown): Pack[] | null {
  const list = Array.isArray((json as { packs?: unknown })?.packs) ? (json as { packs: unknown[] }).packs : Array.isArray(json) ? json : null;
  if (!list) return null;
  const out: Pack[] = [];
  for (const raw of list) {
    const o = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
    const ok = typeof o.id === "string" && typeof o.name === "string" && Number.isInteger(o.credits) && Number.isInteger(o.usdCents) && (o.credits as number) > 0 && (o.usdCents as number) > 0;
    if (ok) out.push({ id: o.id as string, name: o.name as string, credits: o.credits as number, usdCents: o.usdCents as number });
  }
  return out.some((p) => p.id === "creator") && out.some((p) => p.id === "pro") ? out : null;
}

export async function fetchPacks(fetcher: typeof fetch = fetch, timeoutMs = 5000): Promise<Pack[] | null> {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const t = setTimeout(() => ctl?.abort(), timeoutMs);
  try {
    const r = await fetcher("/api/credits/packs", { credentials: "omit", headers: { accept: "application/json" }, signal: ctl?.signal });
    return r.ok ? parsePacks(await r.json()) : null;
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

const WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty"];
/** "about six", "about twenty": how many videos of a given cost a pack buys, to the nearest one. */
export function about(credits: number, each: number): string {
  const n = Math.round(credits / each);
  return `about ${WORDS[n] ?? n}`;
}

const [STILLS, CLIPS] = AD_PRICES.map((a) => a.credits) as [number, number];

/** What a card pack buys, in 30-second videos (from the "What a 30-second video costs" prices). */
export function packLines(p: Pack): string[] {
  const lines = [`${cap(about(p.credits, STILLS))} 30-second videos from animated stills`];
  if (p.credits >= CLIPS * 3) lines[0] = `${lines[0]}, or ${about(p.credits, CLIPS).replace("about ", "")} with AI video in every scene`;
  lines.push("Every kind of video, in 9:16, 1:1 or 16:9", "Pay once: no subscription");
  return lines;
}

export const usdOf = (cents: number) => `$${cents % 100 ? (cents / 100).toFixed(2) : cents / 100}`;
const cap = (s: string) => s[0]!.toUpperCase() + s.slice(1);
